import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { ArrowLeft, Send, Paperclip, X, Check, CheckCheck, Clock3 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import UserAvatar from '../components/ui/UserAvatar';
import { GET_MESSAGE_THREAD } from '../graphql/queries/messageThread';
import { CREATE_MESSAGE } from '../graphql/mutations/createMessage';
import { MARK_MESSAGE_THREAD_READ } from '../graphql/mutations/markMessageThreadRead';

interface Message {
  id: string;
  sender: {
    id: string;
    name: string;
  };
  content: string;
  sentAt: string;
  deliveredAt?: string | null;
  receiverReadAt?: string | null;
  attachments?: {
    id: string;
    url: string;
    filename: string;
    contentType: string;
  }[];
}

interface Participant {
  id: string;
  name: string;
  avatarImageUrl?: string | null;
  isOnline?: boolean | null;
  lastActiveAt?: string | null;
}

interface Tool {
  id: string;
  name: string;
  primaryImageUrl?: string | null;
}

interface MessageThread {
  id: string;
  subject: string;
  tool?: Tool | null;
  participants: Participant[];
  messages: Message[];
  unreadMessageCount: number;
}

interface StatusCheckmarkProps {
  tooltip: string;
  children: React.ReactNode;
}

const StatusCheckmark: React.FC<StatusCheckmarkProps> = ({ tooltip, children }) => {
  return (
    <span className="group relative inline-flex items-center align-middle leading-none" tabIndex={0}>
      <span aria-label={tooltip} className="inline-flex items-center">{children}</span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full right-0 z-20 mb-1 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg group-hover:opacity-100 group-focus:opacity-100"
      >
        {tooltip}
      </span>
    </span>
  );
};

const ViewConversation: React.FC = () => {
  const MAX_MESSAGE_INPUT_HEIGHT = 220;
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newMessage, setNewMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const visibleUnreadIncomingMessageIdsRef = useRef<Set<string>>(new Set());
  const isMarkingThreadReadRef = useRef(false);
  const hasMarkedCurrentUnreadSetRef = useRef(false);
  
  const { data, loading, error, refetch } = useQuery(GET_MESSAGE_THREAD, {
    variables: { id },
    skip: !id,
  });
  
  const [createMessage] = useMutation(CREATE_MESSAGE);
  const [markMessageThreadRead] = useMutation(MARK_MESSAGE_THREAD_READ);
  
  const thread: MessageThread | null = data?.messageThread || null;
  const otherParticipant = thread?.participants.find(p => p.id !== user?.id);

  const unreadIncomingMessages = useMemo(
    () =>
      (thread?.messages || []).filter(
        (message) => message.sender.id !== user?.id && !message.receiverReadAt
      ),
    [thread?.messages, user?.id]
  );

  const unreadIncomingMessageIds = useMemo(
    () => unreadIncomingMessages.map((message) => message.id),
    [unreadIncomingMessages]
  );

  const unreadIncomingSignature = useMemo(
    () => unreadIncomingMessageIds.join('|'),
    [unreadIncomingMessageIds]
  );

  const isOtherParticipantOnline = !!otherParticipant?.isOnline;
  const otherParticipantLastActiveAt = otherParticipant?.lastActiveAt || null;
  
  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  };

  const resizeMessageInput = (target?: HTMLTextAreaElement | null) => {
    const textarea = target || messageInputRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, MAX_MESSAGE_INPUT_HEIGHT);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > MAX_MESSAGE_INPUT_HEIGHT ? 'auto' : 'hidden';
  };
  
  useEffect(() => {
    if (thread?.messages) {
      scrollToBottom('auto');
    }
  }, [thread?.messages]);

  useEffect(() => {
    resizeMessageInput();
  }, [newMessage]);

  useEffect(() => {
    visibleUnreadIncomingMessageIdsRef.current.clear();
    hasMarkedCurrentUnreadSetRef.current = false;
  }, [thread?.id, unreadIncomingSignature]);

  useEffect(() => {
    const messageThreadId = thread?.id;
    const container = messagesContainerRef.current;

    if (!messageThreadId || !container || unreadIncomingMessageIds.length === 0) {
      return;
    }

    const targets = Array.from(
      container.querySelectorAll<HTMLElement>('[data-unread-incoming="true"]')
    );

    if (targets.length === 0) {
      return;
    }

    const tryMarkThreadRead = async () => {
      if (
        isMarkingThreadReadRef.current ||
        hasMarkedCurrentUnreadSetRef.current ||
        unreadIncomingMessageIds.length === 0
      ) {
        return;
      }

      const allUnreadAreVisible = unreadIncomingMessageIds.every((messageId) =>
        visibleUnreadIncomingMessageIdsRef.current.has(messageId)
      );

      if (!allUnreadAreVisible) {
        return;
      }

      isMarkingThreadReadRef.current = true;

      try {
        const { data: mutationData } = await markMessageThreadRead({
          variables: { messageThreadId },
        });

        const mutationErrors = mutationData?.markMessageThreadRead?.errors || [];
        if (mutationErrors.length === 0) {
          hasMarkedCurrentUnreadSetRef.current = true;
          await refetch();
        }
      } catch (markReadError) {
        console.error('Failed to mark thread as read:', markReadError);
      } finally {
        isMarkingThreadReadRef.current = false;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const messageId = entry.target.getAttribute('data-message-id');
          if (!messageId) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            visibleUnreadIncomingMessageIdsRef.current.add(messageId);
          } else {
            visibleUnreadIncomingMessageIdsRef.current.delete(messageId);
          }
        });

        void tryMarkThreadRead();
      },
      {
        root: container,
        threshold: [0.6],
      }
    );

    targets.forEach((target) => observer.observe(target));

    // Run an initial visibility check for messages already in view on open.
    const containerRect = container.getBoundingClientRect();
    targets.forEach((target) => {
      const targetRect = target.getBoundingClientRect();
      const visibleHeight =
        Math.min(targetRect.bottom, containerRect.bottom) -
        Math.max(targetRect.top, containerRect.top);
      const isMostlyVisible = visibleHeight > targetRect.height * 0.6;
      const messageId = target.getAttribute('data-message-id');

      if (messageId && isMostlyVisible) {
        visibleUnreadIncomingMessageIdsRef.current.add(messageId);
      }
    });

    void tryMarkThreadRead();

    return () => {
      observer.disconnect();
    };
  }, [thread?.id, unreadIncomingSignature, markMessageThreadRead, refetch, unreadIncomingMessageIds]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }
  
  if (error || !thread) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Samtale ikke fundet</h2>
          <p className="text-gray-600 mb-4">Den ønskede samtale kunne ikke findes.</p>
          <Button variant="primary" onClick={() => navigate(-1)}>Tilbage til beskeder</Button>
        </div>
      </div>
    );
  }
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && selectedFiles.length === 0) return;
    if (isSending) return;
    
    setIsSending(true);
    
    try {
      // Prepare attachments if any files selected
      const attachments = selectedFiles.length > 0 ? await Promise.all(
        selectedFiles.map(async (file) => {
          // Convert file to base64
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64String = reader.result as string;
              // Remove data URL prefix
              resolve(base64String.split(',')[1]);
            };
            reader.readAsDataURL(file);
          });
          
          return {
            filename: file.name,
            contentType: file.type,
            base64: base64,
          };
        })
      ) : undefined;
      
      const { data } = await createMessage({
        variables: {
          threadId: id,
          content: newMessage.trim() || null,
          attachments,
        },
      });
      
      if (data?.createMessage?.errors && data.createMessage.errors.length > 0) {
        toast.error(data.createMessage.errors.join(', '));
      } else {
        setNewMessage('');
        setSelectedFiles([]);
        await refetch();
        toast.success('Besked sendt!');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Der opstod en fejl ved afsendelse af beskeden');
    } finally {
      setIsSending(false);
    }
  };
  
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('da-DK', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('da-DK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const formatDateTimeTooltip = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'I dag';
    } else if (diffInDays === 1) {
      return 'I går';
    } else if (diffInDays < 7) {
      return date.toLocaleDateString('da-DK', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('da-DK', { month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  const formatLastActive = (timestamp: string | null) => {
    if (!timestamp) return 'Offline';

    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes <= 0) return 'Online';
    if (diffInMinutes < 60) return `Sidst aktiv ${diffInMinutes} min siden`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Sidst aktiv ${diffInHours} t siden`;

    return `Sidst aktiv ${date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })}`;
  };
  
  // Group messages by date
  const groupedMessages = thread.messages.reduce((groups: { [key: string]: Message[] }, message) => {
    const date = formatDate(message.sentAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="mr-4 text-gray-600 hover:text-gray-800">
              <ArrowLeft className="h-6 w-6" />
            </button>
            
            <Link 
              to={`/profile/${otherParticipant?.id}`}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <UserAvatar user={otherParticipant as any} size="md" />
              
              <div className="ml-3">
                <h1 className="text-lg font-medium text-gray-900">{otherParticipant?.name}</h1>
                <div className="flex items-center text-sm mt-0.5">
                  <span
                    className={`h-2 w-2 rounded-full mr-2 ${
                      isOtherParticipantOnline ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  ></span>
                  <span className={isOtherParticipantOnline ? 'text-green-600' : 'text-gray-500'}>
                    {isOtherParticipantOnline ? 'Online' : formatLastActive(otherParticipantLastActiveAt)}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <span>Samtale om: {thread.subject}</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Tool Context Banner */}
      {thread.tool && (
        <div className="flex-shrink-0 bg-green-50 border-b border-green-200 px-4 py-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {thread.tool.primaryImageUrl ? (
                <img
                  src={thread.tool.primaryImageUrl}
                  alt={thread.tool.name}
                  className="h-10 w-10 rounded-lg object-cover"
                />
              ) : (
                <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 font-medium text-sm">🔧</span>
                </div>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {thread.tool.name}
              </p>
              <p className="text-xs text-green-600">
                <Link to={`/tool/${thread.tool.id}`} className="hover:underline">
                  Se værktøjsdetaljer
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-2">
        <div className="max-w-4xl mx-auto">
          {Object.entries(groupedMessages).map(([date, messages]) => (
            <div key={date}>
              <div className="flex items-center justify-center my-2">
                <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                  {date}
                </div>
              </div>

              {messages.map((message, index) => {
                const isOwnMessage = message.sender.id === user?.id;
                const showAvatar = !isOwnMessage && (index === 0 || messages[index - 1]?.sender.id !== message.sender.id);
                const showTime = index === messages.length - 1 || messages[index + 1]?.sender.id !== message.sender.id;

                return (
                  <div
                    key={message.id}
                    data-message-id={message.id}
                    data-unread-incoming={!isOwnMessage && !message.receiverReadAt ? 'true' : undefined}
                    className={`flex mb-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isOwnMessage && (
                      <div className="flex-shrink-0 mr-3">
                        {showAvatar ? (
                          <UserAvatar user={otherParticipant as any} size="sm" />
                        ) : (
                          <div className="h-8 w-8"></div>
                        )}
                      </div>
                    )}

                    <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-1' : ''}`}>
                      <div
                        className={`px-4 py-2 rounded-2xl ${
                          isOwnMessage
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-900'
                        }`}
                      >
                        {message.content && (
                          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                        )}

                        {message.attachments && message.attachments.length > 0 && (
                          <div className={`${message.content ? 'mt-2' : ''} space-y-2`}>
                            {message.attachments.map((attachment) => {
                              const isImage = attachment.contentType.startsWith('image/');

                              return (
                                <div key={attachment.id}>
                                  {isImage ? (
                                    <a
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      <img
                                        src={attachment.url}
                                        alt={attachment.filename}
                                        className="max-w-full rounded-lg"
                                        style={{ maxHeight: '300px' }}
                                      />
                                    </a>
                                  ) : (
                                    <a
                                      href={attachment.url}
                                      download={attachment.filename}
                                      className={`flex items-center gap-2 p-2 rounded-lg ${
                                        isOwnMessage
                                          ? 'bg-green-700 hover:bg-green-800'
                                          : 'bg-gray-100 hover:bg-gray-200'
                                      } transition-colors`}
                                    >
                                      <Paperclip className="h-4 w-4" />
                                      <span className="text-sm truncate">{attachment.filename}</span>
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {showTime && (
                        <div className={`mt-1 flex items-center gap-1 text-xs text-gray-500 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <span>{formatTime(message.sentAt)}</span>
                          {isOwnMessage && (
                            <span className="inline-flex items-center gap-0.5 leading-none">
                              <StatusCheckmark tooltip={`Sendt: ${formatDateTimeTooltip(message.sentAt)}`}>
                                <Check className="h-3.5 w-3.5" />
                              </StatusCheckmark>
                              {message.receiverReadAt && (
                                <StatusCheckmark tooltip={`Læst: ${formatDateTimeTooltip(message.receiverReadAt)}`}>
                                  <CheckCheck className="h-3.5 w-3.5 text-green-700" />
                                </StatusCheckmark>
                              )}
                              {!message.receiverReadAt && (
                                <StatusCheckmark tooltip="Ikke læst endnu">
                                  <Clock3 className="h-3.5 w-3.5 text-gray-500" />
                                </StatusCheckmark>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Message Input */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3">
        <div className="max-w-4xl mx-auto">
          {selectedFiles.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 text-sm"
                >
                  <Paperclip className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-700 max-w-[150px] truncate">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
              className="flex-shrink-0 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Paperclip className="h-5 w-5" />
            </button>

            <div className="flex-1 relative">
              <textarea
                ref={messageInputRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  resizeMessageInput(e.currentTarget);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                disabled={isSending}
                placeholder="Skriv en besked..."
                rows={1}
                className="block w-full resize-none rounded-2xl border-2 border-gray-300 px-4 py-2.5 pr-12 focus:border-green-500 focus:ring-2 focus:ring-green-500 text-base transition-all bg-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '44px', maxHeight: `${MAX_MESSAGE_INPUT_HEIGHT}px` }}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || isSending}
              isLoading={isSending}
              className="flex-shrink-0 rounded-full p-3"
            >
              <Send className="h-5 w-5" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ViewConversation;